import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, FileText, Lock, UserPlus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/lang-context";
import { useAuth } from "@/lib/auth-context";
import { startCreditCheckout, startPremiumCheckout } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * Two-option paywall surfaced when the visitor's quota is exhausted (and
 * after the analyze endpoint returns 402). Always shows BOTH options:
 *
 *   1) Buy 1 report — €1.99 (one-time, server-side amount, redirects to
 *      Stripe Checkout).
 *   2) Go Premium — €9.90 (30 days of unlimited reports).
 *
 * For guests, the modal redirects to login first (Stripe checkout requires
 * an authenticated user so the credit / premium grant can attach to a
 * persistent account). This is enforced by the backend too — we just save
 * the user a click.
 */
const PaywallModal = ({ open, onClose }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null); // "credit" | "premium" | null

  if (!open) return null;

  const handle = async (kind) => {
    trackEvent("paywall_clicked", {
      kind,
      logged_in: !!user,
    });
    if (!user) {
      // Stripe checkout requires login (credits / premium attach to user_id).
      trackEvent("login_redirect", { source: "paywall", kind });
      toast.info(t("paywall.loginFirst"));
      onClose?.();
      navigate("/login");
      return;
    }
    setLoading(kind);
    try {
      const fn = kind === "credit" ? startCreditCheckout : startPremiumCheckout;
      const { url } = await fn(window.location.origin);
      if (!url) throw new Error("no_url");
      trackEvent("checkout_started", {
        kind,
        value: kind === "credit" ? 1.99 : 9.9,
        currency: "EUR",
      });
      // Full-page redirect — Stripe Checkout takes over. After payment the
      // user lands on /billing/return?session_id=… which polls for status.
      window.location.href = url;
    } catch (err) {
      setLoading(null);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("paywall.failed"));
    }
  };

  return (
    <div
      data-testid="paywall-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-[#282C2A]/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="paywall-backdrop"
      />
      <div className="relative bg-[#F7F6F2] border-2 border-[#282C2A] shadow-paper max-w-2xl w-full p-6 md:p-10 fade-up">
        <button
          type="button"
          onClick={onClose}
          data-testid="paywall-close"
          className="absolute top-3 right-3 p-2 text-[#6C706D] hover:text-[#282C2A]"
          aria-label="Close"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        <div className="label-eyebrow inline-flex items-center gap-2 mb-4">
          <Lock size={11} strokeWidth={1.75} />
          {user ? t("paywall.eyebrow") : t("paywall.guestEyebrow")}
        </div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-[#282C2A] leading-tight">
          {user ? t("paywall.title") : t("paywall.guestTitle")}
        </h2>
        <p className="mt-3 text-sm text-[#6C706D] max-w-xl leading-relaxed">
          {user ? t("paywall.sub") : t("paywall.guestSub")}
        </p>

        {/* Guest-only: prominent free registration CTA. Registering grants
            3 free reports (vs 1 for guests) with zero friction. */}
        {!user && (
          <button
            type="button"
            onClick={() => {
              trackEvent("paywall_clicked", { kind: "register_free", logged_in: false });
              onClose?.();
              navigate("/login");
            }}
            data-testid="paywall-register-free"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#4A5D23] text-white font-semibold tracking-wider uppercase text-sm border border-[#3B4A1C] hover:bg-[#3B4A1C] transition-colors shadow-[0_3px_0_#282C2A] hover:shadow-[0_2px_0_#282C2A] hover:translate-y-[1px]"
          >
            <UserPlus size={16} strokeWidth={1.75} />
            {t("paywall.registerFreeCta")}
            <ArrowRight size={16} strokeWidth={1.5} />
          </button>
        )}

        <div className="grid md:grid-cols-2 gap-4 mt-8">
          {/* One-time credit */}
          <div
            data-testid="paywall-credit-card"
            className="border border-[#E6E4DD] bg-white p-5 flex flex-col"
          >
            <div className="flex items-center gap-2 text-[#282C2A]">
              <FileText size={14} strokeWidth={1.75} />
              <span className="label-eyebrow">{t("paywall.creditEyebrow")}</span>
            </div>
            <h3 className="font-serif-display text-2xl mt-2 text-[#282C2A]">
              {t("paywall.creditTitle")}
            </h3>
            <p className="mt-1 text-xs text-[#6C706D]">
              {t("paywall.creditSub")}
            </p>
            <div className="mt-4 font-serif-display text-3xl text-[#282C2A]">
              €1.99
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C706D] ml-2">
                {t("paywall.creditUnit")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handle("credit")}
              disabled={loading != null}
              data-testid="paywall-buy-credit"
              className="mt-auto pt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border border-[#282C2A] text-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors disabled:opacity-50"
            >
              {loading === "credit" ? t("paywall.loading") : t("paywall.creditCta")}
            </button>
          </div>

          {/* Premium */}
          <div
            data-testid="paywall-premium-card"
            className="border-2 border-[#4A5D23] bg-white p-5 flex flex-col relative"
          >
            <div className="absolute top-0 right-4 -translate-y-1/2 bg-[#4A5D23] text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
              {t("paywall.premiumBadge")}
            </div>
            <div className="flex items-center gap-2 text-[#4A5D23]">
              <Sparkles size={14} strokeWidth={1.75} />
              <span className="label-eyebrow text-[#4A5D23]">{t("paywall.premiumEyebrow")}</span>
            </div>
            <h3 className="font-serif-display text-2xl mt-2 text-[#282C2A]">
              {t("paywall.premiumTitle")}
            </h3>
            <p className="mt-1 text-xs text-[#6C706D]">
              {t("paywall.premiumSub")}
            </p>
            <div className="mt-4 font-serif-display text-3xl text-[#282C2A]">
              €9.90
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C706D] ml-2">
                {t("paywall.premiumUnit")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handle("premium")}
              disabled={loading != null}
              data-testid="paywall-buy-premium"
              className="mt-auto pt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors disabled:opacity-50"
            >
              {loading === "premium" ? t("paywall.loading") : t("paywall.premiumCta")}
            </button>
          </div>
        </div>

        <p className="mt-5 text-[11px] text-[#6C706D] text-center">
          {t("paywall.footer")}
        </p>
      </div>
    </div>
  );
};

export default PaywallModal;
